/**
 * Export.jsx
 * Take everything with you.
 *
 * The reason this exists: until now there was no way to get anything out of
 * this app. Two years of captions, what each one earned, and every picture
 * made for it lived in one database with no door. That is fine until it isn't
 * — an account problem, a change of tools, or simply wanting to do your own
 * sums in a spreadsheet.
 *
 * The archive is built here rather than on the server for two reasons: the ZIP
 * writer already exists for the Resize screen, and the pictures can be fetched
 * straight from the media endpoint instead of making a second trip through the
 * Worker. It also means a slow export is a busy tab rather than a Worker
 * sitting at its time limit.
 */

import { useEffect, useState } from 'react';
import { useBrands, useSelectedBrand } from '../hooks/useBrands.jsx';
import { api } from '../services/api.js';
import NoBrands from '../components/NoBrands.jsx';
import { createZip } from '../utils/zip.js';
import { Download, Check, Warning, Image as ImageIcon } from '../components/Icons.jsx';

const readable = (bytes) => {
  if (!bytes) return '0 KB';
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

/**
 * A file name that is safe on every platform and unique within the archive.
 *
 * Two pictures called "logo.png" would silently become one file. The id is
 * appended only where a name has already been used, so the common case stays
 * readable.
 */
function uniqueName(name, id, used) {
  const safe = String(name || `${id}.jpg`).replace(/[\\/:*?"<>|]/g, '-');
  if (!used.has(safe.toLowerCase())) {
    used.add(safe.toLowerCase());
    return safe;
  }
  const dot = safe.lastIndexOf('.');
  const stem = dot > 0 ? safe.slice(0, dot) : safe;
  const ext = dot > 0 ? safe.slice(dot) : '';
  return `${stem}-${id.slice(-6)}${ext}`;
}

export default function Export() {
  const { brands, loading, error: brandsError } = useBrands();
  const [brandId, setBrandId] = useSelectedBrand();

  const [everything, setEverything] = useState(false);
  const [withImages, setWithImages] = useState(true);
  const [summary, setSummary] = useState(null);
  const [progress, setProgress] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(null);

  // What is in there, before committing to downloading it.
  useEffect(() => {
    let cancelled = false;
    setSummary(null);
    setDone(null);
    if (!brandId && !everything) return undefined;

    api.settings
      .exportPreview(everything ? null : brandId)
      .then((d) => {
        if (!cancelled) setSummary(d);
      })
      .catch((e) => {
        if (!cancelled) setError(e);
      });

    return () => {
      cancelled = true;
    };
  }, [brandId, everything]);

  async function download() {
    setBusy(true);
    setError(null);
    setDone(null);

    try {
      const data = await api.settings.exportPreview(everything ? null : brandId);
      const encoder = new TextEncoder();
      const entries = data.files.map((f) => ({
        name: f.name,
        bytes: encoder.encode(f.content),
      }));

      if (withImages) {
        const used = new Set();
        let fetched = 0;

        for (const item of data.images) {
          setProgress({
            done: fetched,
            total: data.images.length,
            name: item.original_name ?? item.id,
          });
          try {
            const res = await fetch(api.media.rawUrl(item.id));
            if (!res.ok) throw new Error(`${res.status}`);
            entries.push({
              name: `images/${uniqueName(item.original_name, item.id, used)}`,
              bytes: new Uint8Array(await res.arrayBuffer()),
            });
          } catch {
            // One unreadable picture must not cost you the other two hundred.
            // It is already listed in images.csv, so nothing goes unrecorded.
          }
          fetched += 1;
        }
      }

      setProgress({ done: entries.length, total: entries.length, name: 'building the archive' });
      const zip = await createZip(entries, {
        onProgress: (d, t) => setProgress({ done: d, total: t, name: 'building the archive' }),
      });

      const url = URL.createObjectURL(zip);
      const link = document.createElement('a');
      const label = everything ? 'everything' : brands.find((b) => b.id === brandId)?.name ?? 'export';
      link.href = url;
      link.download = `kreeboom-${label.toLowerCase().replace(/\W+/g, '-')}-${new Date()
        .toISOString()
        .slice(0, 10)}.zip`;
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);

      setDone(`${entries.length} files, ${readable(zip.size)}.`);
    } catch (e) {
      setError(e);
    } finally {
      setProgress(null);
      setBusy(false);
    }
  }

  if (!loading && brands.length === 0) return <NoBrands error={brandsError} />;

  const sheets = summary?.files?.filter((f) => f.name.endsWith('.csv')) ?? [];

  return (
    <>
      <div className="page-head">
        <div>
          <div className="page-head__eyebrow">Export</div>
          <h1>Take everything with you</h1>
          <p>
            Every post, caption, figure and picture, as spreadsheets you can open in Excel or
            Numbers. Nothing here is locked in.
          </p>
        </div>
        <div className="page-head__actions">
          <select
            value={everything ? '' : brandId}
            onChange={(e) => {
              setEverything(e.target.value === '');
              if (e.target.value) setBrandId(e.target.value);
            }}
          >
            <option value="">Every brand</option>
            {brands.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {error && (
        <div className="notice notice--error">
          <Warning />
          <span>{error.message}</span>
        </div>
      )}

      {progress && (
        <div className="notice notice--info">
          <Check />
          <span>
            {progress.done} of {progress.total} — {progress.name}
          </span>
        </div>
      )}

      {done && (
        <div className="notice notice--info">
          <Check />
          <span>Downloaded. {done}</span>
        </div>
      )}

      <section className="card">
        <div className="card__head">
          <span className="tile">
            <Download size={21} />
          </span>
          <div className="card__head-text">
            <h2>What you will get</h2>
            <p>
              One ZIP file. Times are in UTC, which is what the database stores — an hour behind
              what the app shows you in summer.
            </p>
          </div>
        </div>

        {!summary ? (
          <p className="hint">Reading…</p>
        ) : (
          <>
            <div className="rows">
              {sheets.map((f) => (
                <div className="row" key={f.name}>
                  <div>
                    <div className="row__label">{f.name}</div>
                    <div className="row__sub">
                      {f.rows} row{f.rows === 1 ? '' : 's'}
                    </div>
                  </div>
                </div>
              ))}

              <div className="row">
                <div>
                  <div className="row__label">images/</div>
                  <div className="row__sub">
                    {summary.images.length} file{summary.images.length === 1 ? '' : 's'} ·{' '}
                    {readable(summary.totalBytes)}
                  </div>
                </div>
                <div className="row__end">
                  <button
                    className={`btn btn--sm ${withImages ? 'btn--primary' : ''}`}
                    onClick={() => setWithImages((w) => !w)}
                    disabled={busy}
                  >
                    <ImageIcon size={15} /> {withImages ? 'Included' : 'Left out'}
                  </button>
                </div>
              </div>
            </div>

            {summary.problems?.length > 0 && (
              <div className="notice notice--error">
                <Warning />
                <span>
                  Some parts could not be read and are missing from the export:{' '}
                  {summary.problems.join('; ')}
                </span>
              </div>
            )}

            <p className="hint">
              Access tokens, the Claude connector tokens and anything else that could post as you
              are deliberately left out. An export ends up in a downloads folder; it should not be
              able to publish.
            </p>

            <div className="btn-row">
              <button className="btn btn--primary" onClick={download} disabled={busy}>
                <Download size={15} />
                {busy ? 'Building…' : 'Download it'}
              </button>
            </div>
          </>
        )}
      </section>
    </>
  );
}
